import { useTranslation } from 'react-i18next';
import { getLanguageName } from '@/utils/languages';
import { Modal } from '@/components/ui/Modal';
import { Toggle } from '@/components/ui/Toggle';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useVariationSettings, useUpdateVariationSettings } from '@/hooks/useVariationSettings';
import { cn } from '@/utils/cn';

const LANGUAGE_OPTIONS = [
  { code: 'EN', label: 'English' },
  { code: 'ES', label: 'Spanish' },
  { code: 'DE', label: 'German' },
  { code: 'FR', label: 'French' },
  { code: 'IT', label: 'Italian' },
  { code: 'PT', label: 'Portuguese' },
  { code: 'NL', label: 'Dutch' },
  { code: 'JA', label: 'Japanese' },
  { code: 'ZH', label: 'Chinese' },
  { code: 'AR', label: 'Arabic' },
  { code: 'RU', label: 'Russian' },
  { code: 'PL', label: 'Polish' },
  { code: 'KO', label: 'Korean' },
];

interface VariationSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const VariationSettingsModal = ({ open, onOpenChange }: VariationSettingsModalProps) => {
  const { t } = useTranslation('common');
  const autoTranslateLock = useFeatureLock('auto_translate');
  const { user, refreshUser } = useAuth();
  const teamId = user?.currentTeamId ?? user?.teamId ?? 0;

  const { data: settings, isLoading } = useVariationSettings(teamId);
  const mutation = useUpdateVariationSettings();

  const handleToggleEnabled = () => {
    if (!settings) return;
    mutation.mutate(
      { teamId, data: { variationsEnabled: !settings.variationsEnabled } },
      { onSuccess: () => void refreshUser() }
    );
  };

  const handleToggleLanguage = (code: string) => {
    if (!settings) return;
    const current = settings.variationLanguages ?? [];
    const updated = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    mutation.mutate({ teamId, data: { variationLanguages: updated } });
  };

  const handleToggleAutoTranslate = () => {
    if (!settings) return;
    mutation.mutate({ teamId, data: { autoTranslateEnabled: !settings.autoTranslateEnabled } });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('variation_settings_title', { defaultValue: 'Variation Settings' })}
      size="lg"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Icon name={ICONS.LOADER} size="md" className="animate-spin text-gray-400" ariaHidden />
        </div>
      ) : (
        <div className="space-y-0">
          {/* Setting 1: Enable Variations */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('variation_settings_enabled_label', { defaultValue: 'Enable Variations' })}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('variation_settings_enabled_description', {
                  defaultValue: 'Create language and custom variations of your posts',
                })}
              </p>
            </div>
            <Toggle
              checked={settings?.variationsEnabled ?? false}
              onChange={handleToggleEnabled}
              label={t('variation_settings_enabled_label', { defaultValue: 'Enable Variations' })}
            />
          </div>

          {settings?.variationsEnabled && (
            <>
              {/* Setting 2: Variation Languages */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <p className="text-sm font-medium text-gray-900">
                  {t('variation_settings_languages_label', { defaultValue: 'Variation Languages' })}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">
                  {t('variation_settings_languages_description', {
                    defaultValue: 'Select languages for automatic variation creation',
                  })}
                </p>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGE_OPTIONS.map(({ code }) => {
                    const isSelected = settings.variationLanguages?.includes(code) ?? false;
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => handleToggleLanguage(code)}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors duration-150',
                          isSelected
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        )}
                        aria-pressed={isSelected}
                        aria-label={getLanguageName(code)}
                      >
                        {isSelected && (
                          <Icon name={ICONS.CHECK} size="xs" ariaHidden />
                        )}
                        <span>{code}</span>
                        <span className="text-gray-400">·</span>
                        <span>{getLanguageName(code)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Setting 3: Auto-translate */}
              <div className="border-t border-gray-100 pt-4 mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t('variation_settings_auto_translate_label', { defaultValue: 'Auto-translate' })}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('variation_settings_auto_translate_description', {
                      defaultValue:
                        'Automatically create translated variations when saving posts (uses AI tokens)',
                    })}
                  </p>
                  {autoTranslateLock.locked && (
                    <button
                      type="button"
                      onClick={autoTranslateLock.showUpsell}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
                    >
                      <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                      {autoTranslateLock.label}
                    </button>
                  )}
                </div>
                <Toggle
                  checked={(settings?.autoTranslateEnabled ?? false) && !autoTranslateLock.locked}
                  disabled={autoTranslateLock.locked}
                  onChange={handleToggleAutoTranslate}
                  label={t('variation_settings_auto_translate_label', { defaultValue: 'Auto-translate' })}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};
